import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import Card from 'components/card';
import { useDisclosure } from '@chakra-ui/hooks';
import SuccessModal from '../myOffer/components/SuccessModal';
import { SiGooglesheets } from 'react-icons/si';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

type BodyEntry = { field: string; answer: string };
type Lead = {
    _id: string;
    offerBuyerNumber?: string | number;
    offerId?: string;
    purchaseOrderId?: string;
    isPurchaseOrderLead?: boolean;
    createdAt?: string;
    channels?: string[];
    index?: number;
    totalLeads?: number;
    leadrsRate: number;
    customerRate: number;
    body?: BodyEntry[];
    comment?: string;
    reasons?: string[];
    status?: string; // added so we can pass through to handleLeadChange
    viewed?: boolean; // track if lead has been viewed by buyer
    approvalStatus?: 'pending' | 'approved' | 'rejected' | 'conflict';
};

const REASON_OPTIONS = [
    'Intéressé',
    'À rappeler',
    'Budget insuffisant',
    'Hors cible',
    'Ne répond pas',
    'Mauvaise identité',
    'Numéro invalide',
    'Pas intéressé',
];

const MyLeadsBuyer = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [leads, setLeads] = useState<Lead[]>([]);
    const [selectedOffer, setSelectedOffer] = useState<string>('');
    const [search, setSearch] = useState<string>('');
    const [viewedLeads, setViewedLeads] = useState<Set<string>>(new Set());
    const [offerDetails, setOfferDetails] = useState<Record<string, { type?: string; industry?: string; subIndustry?: string; regions?: string[] }>>({});

    // timers per lead for debounced comment saving
    const commentTimers = useRef<Record<string, any>>({});

    const initUser = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                const user = res.data;
                if (user.kyc_url) {
                    setTimeout(() => {
                        window.location.href = user.kyc_url;
                    }, 3000);
                }
                if (user && user.type !== 'buyer') {
                    window.location.href = '/admin/my-request';
                }
            })
            .catch((err) => console.log('No user info found', err));
    };

    const initMyLeads = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/lead`, config)
            .then((res) => {
                setLeads(res.data || []);
                // Load viewed leads from localStorage
                const stored = localStorage.getItem('viewedLeads');
                if (stored) {
                    setViewedLeads(new Set(JSON.parse(stored)));
                }
            })
            .catch((err) => {
                console.log('No user info found', err);
                window.location.href = '/auth/sign-in';
            });
    };

    const markLeadAsViewed = (leadId: string) => {
        setViewedLeads((prev) => {
            const newSet = new Set(prev);
            newSet.add(leadId);
            // Persist to localStorage
            localStorage.setItem('viewedLeads', JSON.stringify([...newSet]));
            return newSet;
        });
    };

    useEffect(() => {
        if (
            !localStorage.getItem('token') ||
            localStorage.getItem('token')!.length < 10
        ) {
            window.location.href = '/auth/sign-up';
        }
        const urlParams = new URLSearchParams(window.location.search);
        const created = urlParams.get('created');
        if (created === 'true' && !isOpen) onOpen();

        initMyLeads();
        initUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---------- helpers
    const timeAgo = (date: any) => {
        const now: any = new Date();
        const givenDate: any = new Date(date);
        const diffInSeconds = Math.floor((now - givenDate) / 1000);

        const days = Math.floor(diffInSeconds / (3600 * 24));
        const hours = Math.floor((diffInSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((diffInSeconds % 3600) / 60);
        const seconds = diffInSeconds % 60;

        if (days > 0) return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
        if (hours > 0) return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
        if (minutes > 0)
            return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
        return `Il y a ${seconds} seconde${seconds > 1 ? 's' : ''}`;
    };

    const isWithin30Minutes = (date: any) => {
        const start = new Date();
        const end = new Date(date);
        const diffInMs = Math.abs(end.getTime() - start.getTime());
        const diffInMinutes = diffInMs / (1000 * 60);
        return diffInMinutes <= 30;
    };

    const handleCustomerRateChange = (leadId: string, rate: number) => {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        axios
            .put(`${CONFIG.apiUrl}/lead/${leadId}/rate`, { rate }, config)
            .then(() => {
                setLeads((prev) =>
                    prev.map((l) =>
                        l._id === leadId ? { ...l, customerRate: rate } : l
                    )
                );
            })
            .catch((err) => console.log(err));
    };

    const handleLeadChange = (
        leadId: string,
        status: string,
        comment: string
    ) => {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        return axios
            .put(
                `${CONFIG.apiUrl}/lead/${leadId}/status`,
                { status, comment },
                config
            )
            .then(() => {
                setLeads((prev) =>
                    prev.map((l) =>
                        l._id === leadId ? { ...l, status, comment } : l
                    )
                );
            });
    };

    const handleLeadProblem = async (leadId: string) => {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        try {
            await axios.put(
                `${CONFIG.apiUrl}/lead/${leadId}/approval-status`,
                { approvalStatus: 'conflict' },
                config
            );
            setLeads((prev) =>
                prev.map((l) =>
                    l._id === leadId ? { ...l, approvalStatus: 'conflict' } : l
                )
            );
            toast.success('Problème signalé', { autoClose: 1800 });
        } catch (err) {
            toast.error('Échec du signalement ❌');
        }
    };

    // debounced comment saver which also calls handleLeadChange
    const scheduleCommentSave = (lead: Lead, newComment: string) => {
        // optimistic local update so the UI reflects typing immediately
        setLeads((prev) =>
            prev.map((l) =>
                l._id === lead._id ? { ...l, comment: newComment } : l
            )
        );

        const key = lead._id;
        if (commentTimers.current[key])
            clearTimeout(commentTimers.current[key]);

        commentTimers.current[key] = setTimeout(async () => {
            try {
                await handleLeadChange(lead._id, lead.status ?? '', newComment);
                toast.success('Commentaire enregistré ✅', { autoClose: 1800 });
            } catch (e) {
                toast.error("Échec de l'enregistrement du commentaire ❌");
                // optional: rollback could go here
            }
        }, 800); // 800ms debounce
    };

    // ---------- offer filter + search
    const offerKey = (l: Lead) =>
        (l.purchaseOrderId && `po_${l.purchaseOrderId}`) ||
        (l.offerId && String(l.offerId)) ||
        (l.offerBuyerNumber && String(l.offerBuyerNumber)) ||
        'Unknown';
    const offerLabel = (l: Lead) =>
        (l.purchaseOrderId && `BC-${String(l.purchaseOrderId).slice(-6)}`) ||
        (l.offerBuyerNumber && `#${l.offerBuyerNumber}`) ||
        (l.offerId && l.offerId) ||
        'Unknown';

    const offers = useMemo(() => {
        const map = new Map<string, { label: string; total: number; newCount: number }>();
        leads.forEach((l) => {
            const key = offerKey(l);
            const existing = map.get(key);
            const isNew = !viewedLeads.has(l._id);
            if (existing) {
                existing.total += 1;
                if (isNew) existing.newCount += 1;
            } else {
                map.set(key, { label: offerLabel(l), total: 1, newCount: isNew ? 1 : 0 });
            }
        });
        return Array.from(map.entries()).map(([value, data]) => ({
            value,
            label: data.label,
            total: data.total,
            newCount: data.newCount,
        }));
    }, [leads, viewedLeads]);

    // Auto-select first offer when offers are loaded
    useEffect(() => {
        if (offers.length > 0 && !selectedOffer) {
            setSelectedOffer(offers[0].value);
        }
    }, [offers, selectedOffer]);

    // Fetch offer/purchase-order details (industry, subIndustry, regions) for each unique offer
    const offerIds = useMemo(() => offers.map((o) => o.value).sort().join(','), [offers]);
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token || !offerIds) return;
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const ids = offerIds.split(',').filter((id) => id !== 'ALL' && id !== 'Unknown');
        if (ids.length === 0) return;
        // Valid MongoDB ObjectId: 24 hex chars
        const isObjectId = (s: string) => /^[a-f\d]{24}$/i.test(s);
        Promise.all(
            ids.map((id) => {
                // Purchase order keys are prefixed with "po_"
                if (id.startsWith('po_')) {
                    const poId = id.replace('po_', '');
                    if (!isObjectId(poId)) return null;
                    return axios
                        .get(`${CONFIG.apiUrl}/purchase-order/${poId}`, config)
                        .then((r) => ({ id, data: r.data, isPurchaseOrder: true }))
                        .catch((): null => null);
                }
                if (!isObjectId(id)) return null;
                return axios
                    .get(`${CONFIG.apiUrl}/offer/${id}`, config)
                    .then((r) => ({ id, data: r.data, isPurchaseOrder: false }))
                    .catch((): null => null);
            })
        ).then((results) => {
            const details: Record<string, { type?: string; industry?: string; subIndustry?: string; regions?: string[] }> = {};
            results.forEach((r) => {
                if (r?.data) {
                    if (r.isPurchaseOrder) {
                        details[r.id] = {
                            industry: r.data.industry,
                            subIndustry: r.data.product,
                            regions: r.data.departments[0] ? [r.data.departments[0]] : [],
                        };
                    } else {
                        details[r.id] = {
                            type: r.data.type,
                            industry: r.data.industry,
                            subIndustry: r.data.subIndustry[0] ? r.data.subIndustry[0] : undefined,
                            regions: r.data.regions,
                        };
                    }
                }
            });
            setOfferDetails(details);
        });
    }, [offerIds]);

    const filteredLeads = useMemo(() => {
        let arr = leads;
        if (selectedOffer && selectedOffer !== 'ALL')
            arr = arr.filter((l) => offerKey(l) === selectedOffer);
        if (search.trim()) {
            const q = search.toLowerCase();
            arr = arr.filter((l) => {
                const fromBody =
                    l.body?.some(
                        (e) =>
                            e.field?.toLowerCase().includes(q) ||
                            String(e.answer ?? '')
                                .toLowerCase()
                                .includes(q)
                    ) || false;
                const fromChannels =
                    l.channels?.join(', ').toLowerCase().includes(q) || false;
                const fromId =
                    String(l.offerBuyerNumber ?? '')
                        .toLowerCase()
                        .includes(q) ||
                    String(l.offerId ?? '')
                        .toLowerCase()
                        .includes(q);
                const fromComment = (l.comment ?? '').toLowerCase().includes(q);
                const fromReasons = (l.reasons ?? [])
                    .join(' ')
                    .toLowerCase()
                    .includes(q);
                return (
                    fromBody ||
                    fromChannels ||
                    fromId ||
                    fromComment ||
                    fromReasons
                );
            });
        }
        return arr;
    }, [leads, selectedOffer, search]);

    // ---------- dynamic body columns (only from filtered leads)
    const bodyFields: string[] = useMemo(() => {
        const s = new Set<string>();
        filteredLeads.forEach((l) => l.body?.forEach((e) => e.field && s.add(e.field)));
        return Array.from(s);
    }, [filteredLeads]);

    const getBodyAnswer = (lead: Lead, field: string) =>
        lead.body?.find((e) => e.field === field)?.answer ?? '—';

    // Calculate unviewed leads count
    const unviewedCount = useMemo(() => {
        return filteredLeads.filter((l) => !viewedLeads.has(l._id)).length;
    }, [filteredLeads, viewedLeads]);

    // Auto-mark leads as viewed when they appear in the filtered list
    useEffect(() => {
        if (filteredLeads.length > 0) {
            const timer = setTimeout(() => {
                filteredLeads.forEach((lead) => {
                    if (!viewedLeads.has(lead._id)) {
                        markLeadAsViewed(lead._id);
                    }
                });
            }, 30000); // 30 seconds
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredLeads]);

    // ---------- CSV includes comment + reasons
    const downloadCSV = () => {
        const staticHeaders = [
            'ID Mission',
            'Ma note',
            'Motifs',
            'Commentaire',
            "Canal d'acquisition",
            'Avancement',
            'Note leadrs',
        ];
        const headers = [
            staticHeaders[0],
            staticHeaders[1],
            staticHeaders[2],
            staticHeaders[3],
            ...bodyFields,
            ...staticHeaders.slice(4),
        ];

        const rows = filteredLeads.map((item) => {
            const id = `#${item.offerBuyerNumber ?? ''}`;
            const bodyValues = bodyFields.map((f) => getBodyAnswer(item, f));
            const progress =
                item.totalLeads && item.totalLeads > 1
                    ? `${item.index}/${item.totalLeads}`
                    : 'N/A';
            const motifs = (item.reasons ?? []).join(' / ');
            const comment = item.comment ?? '';
            return [
                id,
                `${item.customerRate}/5`,
                motifs,
                comment,
                ...bodyValues,
                item.channels?.join('/') || '',
                progress,
                `${item.leadrsRate}/10`,
            ];
        });

        const csvContent = [headers, ...rows]
            .map((row) =>
                row
                    .map((field) =>
                        typeof field === 'string'
                            ? `"${field.replace(/"/g, '""')}"`
                            : field
                    )
                    .join(',')
            )
            .join('\n');

        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;',
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'mes_leads.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const LONG_FIELD_HINTS = [
        'description',
        'message',
        'besoin',
        'comment',
        'notes',
        'remarque',
        'détails',
        'details',
        'adresse',
    ];

    const isLongField = (name: string) =>
        LONG_FIELD_HINTS.some(
            (h) => name.toLowerCase().includes(h) || name.length > 30
        );

    const colClassFor = (name: string) =>
        isLongField(name)
            ? 'min-w-[260px] max-w-[540px]'
            : 'min-w-[120px] max-w-[260px]';

    const DiscoverCommandPage = () => (
        <Card extra="w-full pb-8 pt-8 px-8 h-fit shadow-md rounded-[20px] bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
            <div className="flex flex-col gap-4">
                <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-blue-600 mb-3">
                        leadrs.co
                    </p>
                    <h2 className="text-2xl font-bold text-navy-900 mb-4">
                        Mes leads 🎯
                    </h2>
                    <p className="text-base text-gray-700 leading-relaxed max-w-3xl">
                        Consultez vos prospects, identifiez ceux qui présentent le
                        plus grand potentiel et ajustez vos actions pour maximiser
                        vos conversions.
                    </p>
                </div>
            </div>
        </Card>
    );

    const UnviewedLeadsCard = () => (
        unviewedCount > 0 ? (
            <Card extra="w-full pb-4 pt-4 px-6 h-fit shadow-sm rounded-[16px] bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="rounded-full bg-orange-500 p-2">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-orange-900">
                                Nouveaux leads non consultés
                            </p>
                            <p className="text-xs text-orange-700">
                                Vous avez {unviewedCount} nouveau{unviewedCount > 1 ? 'x' : ''} lead{unviewedCount > 1 ? 's' : ''} à consulter
                            </p>
                        </div>
                    </div>
                    <div className="rounded-full bg-orange-500 px-4 py-2">
                        <span className="text-xl font-bold text-white">{unviewedCount}</span>
                    </div>
                </div>
            </Card>
        ) : null
    );

    return (
        <div className="h-full w-full overflow-x-hidden rounded-[20px]">
            {/* Toasts */}
            <ToastContainer position="bottom-right" />

            <SuccessModal isOpen={isOpen} onClose={onClose} />
            <DiscoverCommandPage />
            
            {/* Unviewed Leads Alert */}
            {unviewedCount > 0 && (
                <div className="mt-5">
                    <UnviewedLeadsCard />
                </div>
            )}

            {/* Offer Cards */}
            {offers.length > 0 && (
                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                    {offers.map((o) => (
                        <button
                            key={o.value}
                            onClick={() => setSelectedOffer(o.value)}
                            className={`relative rounded-xl border px-4 py-3 text-left shadow-sm transition-all hover:shadow-md ${
                                selectedOffer === o.value
                                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/30'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                        >
                            {o.newCount > 0 && (
                                <span className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-orange-500 px-1.5 text-[10px] font-bold text-white">
                                    {o.newCount}
                                </span>
                            )}
                            <p className={`text-sm font-semibold ${
                                selectedOffer === o.value ? 'text-blue-700' : 'text-gray-800'
                            }`}>
                                {o.value.startsWith('po_') ? '' : 'Offre'} {o.label.slice(0, 7)}
                            </p>
                            {offerDetails[o.value]?.type && (
                                <span className="mt-1 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 capitalize">
                                    {offerDetails[o.value].type}
                                </span>
                            )}
                            {offerDetails[o.value]?.industry || offerDetails[o.value]?.subIndustry ? (
                                <p className="mt-1 text-xs text-gray-500 truncate">
                                    {[offerDetails[o.value].industry, offerDetails[o.value].subIndustry]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </p>
                            ) : null}
                            {offerDetails[o.value]?.regions && offerDetails[o.value].regions!.length > 0 && (
                                <p className="text-xs text-gray-400 truncate">
                                    {offerDetails[o.value].regions!.join(', ')}
                                </p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">
                                {o.total} lead{o.total > 1 ? 's' : ''} reçu{o.total > 1 ? 's' : ''}
                            </p>
                        </button>
                    ))}
                </div>
            )}

            {/* Controls */}
            <div className="mt-4 flex flex-col items-stretch gap-3 md:flex-row">
                <input
                    type="search"
                    placeholder="Rechercher (ID, canal, champs, motifs, commentaire)…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-[320px] rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-gray-400 focus:outline-none"
                />

                {filteredLeads.length > 0 && (
                    <button
                        onClick={downloadCSV}
                        className="flex items-center gap-2 rounded-md bg-[#0F9D58] px-4 py-2 text-sm font-medium text-white shadow-sm
               hover:bg-[#0b7d46] focus:outline-none focus:ring-2 focus:ring-[#0F9D58]/50"
                    >
                        <SiGooglesheets size={18} className="text-white" />
                        <span>Télécharger (Sheets)</span>
                    </button>
                )}
            </div>

            {/* Table wrapper */}
            {filteredLeads.length === 0 ? (
                <div className="mt-5">
                    <p className="text-center text-lg text-gray-500">
                        Aucun lead trouvé
                    </p>
                </div>
            ) : (
                <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full min-w-[1400px] text-left text-sm text-gray-700">
                        <thead className="sticky top-0 bg-gray-50 text-gray-800">
                            <tr>
                                <th className="sticky left-0 z-50 bg-gray-50 px-2 py-3">
                                    # ID Mission
                                </th>
                                <th className="min-w-[140px] px-2 py-3">
                                    Ma note
                                </th>
                                <th className="min-w-[220px] px-2 py-3">
                                    Motifs
                                </th>
                                <th className="min-w-[220px] px-2 py-3">
                                    Action
                                </th>
                                <th className="min-w-[260px] px-2 py-3">
                                    Commentaire
                                </th>

                                {/* dynamic body fields */}
                                {bodyFields.map((f) => (
                                    <th
                                        key={f}
                                        className={`${colClassFor(
                                            f
                                        )} px-2 py-3`}
                                    >
                                        {f}
                                    </th>
                                ))}

                                <th className="min-w-[200px] px-2 py-3">
                                    Canal d'acquisition
                                </th>
                                <th className="min-w-[120px] px-2 py-3">
                                    Avancement
                                </th>
                                <th className="min-w-[120px] px-2 py-3">
                                    Note leadrs
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLeads.map((item, index) => (
                                <tr
                                    key={item._id || index}
                                    className="border-b border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
                                >
                                    <td className="sticky left-0 z-[1] bg-gray-50 px-2 py-3 align-top shadow-[1px_0_0_#e5e7eb]">
                                        <div className="flex items-center gap-2">
                                            <p className="my-auto flex">
                                                #{item.offerBuyerNumber?.toString().slice(0, 6) || ''}
                                            </p>
                                            {!viewedLeads.has(item._id) && (
                                                <span className="rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white animate-pulse">
                                                    Nouveau
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 flex items-center gap-2">
                                            {item?.createdAt &&
                                                isWithin30Minutes(
                                                    item.createdAt
                                                ) && (
                                                    <span className="relative my-auto flex h-2 w-2">
                                                        <span className="bg-toola-500/50 absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" />
                                                        <span className="bg-toola-500/50 relative inline-flex h-2 w-2 rounded-full" />
                                                    </span>
                                                )}
                                            {item?.createdAt && (
                                                <p className="text-xs text-gray-600">
                                                    {timeAgo(item.createdAt)}
                                                </p>
                                            )}
                                        </div>
                                    </td>

                                    {/* Ma note */}
                                    <td className="px-2 py-3 align-top">
                                        <div className="flex items-center space-x-1">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <button
                                                    key={star}
                                                    onClick={() =>
                                                        handleCustomerRateChange(
                                                            item._id,
                                                            star
                                                        )
                                                    }
                                                    className="text-xl focus:outline-none"
                                                    title={`${star} étoile${
                                                        star > 1 ? 's' : ''
                                                    }`}
                                                >
                                                    <span
                                                        className={
                                                            star <=
                                                            item.customerRate
                                                                ? 'text-yellow-400'
                                                                : 'text-gray-300'
                                                        }
                                                    >
                                                        ★
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </td>

                                    {/* Motifs */}
                                    <td className="px-2 py-3 align-top">
                                        <label
                                            className="sr-only"
                                            htmlFor={`reason-${item._id}`}
                                        >
                                            Motif
                                        </label>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex flex-col gap-2">
                                                <div className="relative inline-block">
                                                    <select
                                                        id={`reason-${item._id}`}
                                                        value={item.reasons?.[0] ?? ''}
                                                        onChange={async (e) => {
                                                            const v = e.target.value;
                                                            try {
                                                                await handleLeadChange(
                                                                    item._id,
                                                                    v || '',
                                                                    item.comment ?? ''
                                                                );
                                                                toast.success(
                                                                    'Motif mis à jour ✅',
                                                                    { autoClose: 1800 }
                                                                );
                                                            } catch (err) {
                                                                toast.error(
                                                                    'Échec de mise à jour du motif ❌'
                                                                );
                                                            }
                                                        }}
                                                        className="h-8 w-[180px] truncate rounded-md border border-gray-300 bg-white pl-3 pr-8 text-xs text-gray-700
                    shadow-sm focus:border-gray-400 focus:outline-none"
                                                    >
                                                        <option value="">
                                                            {item.status
                                                                ? item.status
                                                                : 'Sélectionner un motif'}
                                                        </option>
                                                        {REASON_OPTIONS
                                                        .filter((opt) => opt !== item.status)
                                                        .map((opt) => (
                                                            <option
                                                                key={opt}
                                                                value={opt}
                                                            >
                                                                {opt}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    {/* Signalement */}
                                    <td className="px-2 py-3 align-top">
                                        {item.approvalStatus === 'conflict' ? (
                                            <div className="inline-flex flex-col gap-1 rounded-lg bg-red-100 border border-red-300 px-4 py-2.5 shadow-sm">
                                                <div className="flex items-center gap-2">
                                                    <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="text-xs text-red-800 font-semibold">
                                                        Conflit en cours – Lead signalé
                                                    </span>
                                                </div>
                                                <p className="text-[11px] text-red-700 leading-tight">
                                                    Notre équipe va analyser ce lead.
                                                </p>
                                            </div>
                                        ) : item.approvalStatus === 'approved' ? (
                                            <div className="inline-flex flex-col gap-1 rounded-lg bg-blue-100 border border-blue-300 px-4 py-2.5 shadow-sm">
                                                <div className="flex items-center gap-2">
                                                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="text-xs text-blue-800 font-semibold">
                                                        Lead comptabilisé
                                                    </span>
                                                </div>
                                                <p className="text-[11px] text-blue-700 leading-tight">
                                                    Ce lead est conforme et sera facturé.
                                                </p>
                                            </div>
                                        ) : item.approvalStatus === 'rejected' ? (
                                            <div className="inline-flex flex-col gap-1 rounded-lg bg-green-100 border border-green-300 px-4 py-2.5 shadow-sm">
                                                <div className="flex items-center gap-2">
                                                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="text-xs text-green-800 font-semibold">
                                                        Lead supprimé – Non facturé
                                                    </span>
                                                </div>
                                                <p className="text-[11px] text-green-700 leading-tight">
                                                    La contestation a été acceptée. Aucun paiement ne sera dû.
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                {!item.status && !item.reasons?.[0] ? (
                                                    <div className="inline-flex flex-col gap-1 rounded-lg bg-gray-100 border border-gray-300 px-4 py-2.5 shadow-sm">
                                                        <div className="flex items-center gap-2">
                                                            <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                            </svg>
                                                            <span className="text-xs text-gray-700 font-semibold">
                                                                Action requise
                                                            </span>
                                                        </div>
                                                        <p className="text-[11px] text-gray-600 leading-tight">
                                                            Veuillez d'abord sélectionner un motif avant de signaler un problème.
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleLeadProblem(item._id)}
                                                        className="inline-flex items-center gap-2 rounded-lg bg-white border-2 border-red-500 px-4 py-2.5 shadow-sm hover:bg-red-50 hover:border-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                                                        disabled={false}
                                                    >
                                                        <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="text-xs text-red-600 font-semibold">
                                                            Signaler un problème avec ce lead
                                                        </span>
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </td>

                                    {/* Commentaire */}
                                    <td className="px-2 py-3 align-top">
                                        <textarea
                                            defaultValue={item.comment ?? ''}
                                            placeholder="Ajouter un commentaire…"
                                            onChange={(e) => {
                                                const val =
                                                    e.currentTarget.value;
                                                scheduleCommentSave(item, val);
                                            }}
                                            className="h-20 w-[260px] resize-none rounded-md border border-gray-300 p-2 text-sm focus:border-gray-400 focus:outline-none"
                                        />
                                    </td>

                                    {bodyFields.map((f) => (
                                        <td
                                            key={f}
                                            className="max-w-[320px] px-2 py-3 align-top text-sm"
                                        >
                                            <div className="break-words">
                                                {getBodyAnswer(item, f)}
                                            </div>
                                        </td>
                                    ))}

                                    <td className="max-w-[180px] px-2 py-3 align-top text-xs">
                                        {item.channels?.join(', ')}
                                    </td>

                                    <td className="px-2 py-3 align-top">
                                        {item.totalLeads && item.totalLeads > 1
                                            ? `${item.index}/${item.totalLeads}`
                                            : 'N/A'}
                                    </td>

                                    <td className="px-2 py-3 align-top">
                                        {item.leadrsRate === 0 && (
                                            <span className="rounded-full bg-red-200 px-2 py-1 text-xs text-red-700">
                                                {item.leadrsRate}/10
                                            </span>
                                        )}
                                        {item.leadrsRate >= 7 && (
                                            <span className="rounded-full bg-green-200 px-2 py-1 text-xs text-green-700">
                                                {item.leadrsRate}/10
                                            </span>
                                        )}
                                        {item.leadrsRate < 7 &&
                                            item.leadrsRate > 0 && (
                                                <span className="rounded-full bg-yellow-200 px-2 py-1 text-xs text-yellow-700">
                                                    {item.leadrsRate}/10
                                                </span>
                                            )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default MyLeadsBuyer;
